import { useEffect, useState } from "react";
import { Banknote, Eye, Loader2, RefreshCw, Truck } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_STYLE = {
  pending: "bg-ochre/15 text-ochre",
  packing: "bg-sage-light text-sage",
  dispatched: "bg-terra-light text-terra-dark",
  delivered: "bg-espresso text-cream",
  cancelled: "bg-destructive/10 text-destructive",
};

const inputCls = "h-10 w-full rounded-lg border border-linen bg-white px-3 text-sm outline-none focus:border-terra";

const OrdersTab = () => {
  const [orders, setOrders] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [shiprocket, setShiprocket] = useState({ configured: false });
  const [shipOrder, setShipOrder] = useState(null);
  const [dims, setDims] = useState({ length_cm: 20, breadth_cm: 15, height_cm: 10, weight_kg: 0.5 });
  const [shipping, setShipping] = useState(false);

  const load = () => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => toast.error("Could not load orders"));
    api.get("/shipping/status").then((r) => setShiprocket(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      const r = await api.patch(`/orders/${id}`, { status });
      setOrders((prev) => prev.map((o) => (o.id === id ? r.data : o)));
      toast.success(`Order ${id} → ${status}${status === "dispatched" || status === "delivered" ? " — customer emailed" : ""}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  const markPaid = async (id) => {
    try {
      const r = await api.patch(`/orders/${id}/payment`, { payment_status: "paid" });
      setOrders((prev) => prev.map((o) => (o.id === id ? r.data : o)));
      setViewing((v) => (v && v.id === id ? r.data : v));
      toast.success(`Order ${id} marked as paid`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update payment status");
    }
  };

  const dispatch = async () => {
    setShipping(true);
    try {
      const r = await api.post(`/admin/orders/${shipOrder.id}/dispatch`, {
        length_cm: Number(dims.length_cm), breadth_cm: Number(dims.breadth_cm),
        height_cm: Number(dims.height_cm), weight_kg: Number(dims.weight_kg),
      });
      const updated = r.data.order;
      setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      setShipOrder(null);
      if (r.data.already_dispatched) {
        toast("Shipment already exists for this order");
      } else {
        toast.success(`Shipment created — AWB ${updated.shipping_awb} · customer emailed tracking link`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create shipment");
    } finally {
      setShipping(false);
    }
  };

  if (!orders) return <div className="h-64 animate-pulse rounded-2xl bg-sand" />;

  const canShip = (o) => shiprocket.configured && !o.shipping_awb && !["dispatched", "delivered", "cancelled"].includes(o.status);

  return (
    <div data-testid="admin-orders-tab">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-clay/60">
          {orders.length} orders received
          {!shiprocket.configured && <span className="ml-2 text-ochre">· Shiprocket not connected (shipments pending setup)</span>}
        </p>
        <button onClick={load} className="flex items-center gap-2 rounded-full border border-linen px-4 py-2 text-xs font-semibold text-clay transition-colors hover:border-terra hover:text-terra">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-linen bg-white p-14 text-center">
          <p className="font-serif text-xl text-espresso">No orders yet</p>
          <p className="mt-2 text-sm text-clay/60">New orders from the storefront land here the moment they're placed.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-linen bg-white" data-testid="admin-orders-data-table">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead>
              <tr className="border-b border-linen font-mono text-[10px] uppercase tracking-[0.18em] text-clay/60">
                <th className="px-5 py-3.5">Order</th>
                <th className="px-5 py-3.5">Customer</th>
                <th className="px-5 py-3.5">Items</th>
                <th className="px-5 py-3.5">Total</th>
                <th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-linen/70">
              {orders.map((o) => (
                <tr key={o.id} className="transition-colors hover:bg-sand/40">
                  <td className="px-5 py-3.5">
                    <p className="font-mono font-semibold text-espresso">#{o.id}</p>
                    <p className="font-mono text-[10px] text-clay/50">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                    {o.shipping_awb && <p className="mt-1 font-mono text-[10px] text-sage">AWB {o.shipping_awb}</p>}
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="font-medium text-espresso">{o.customer.name}</p>
                    <p className="text-xs text-clay/60">{o.customer.city}, {o.customer.state}</p>
                  </td>
                  <td className="px-5 py-3.5 text-clay/70">{o.items.reduce((s, i) => s + i.qty, 0)}</td>
                  <td className="px-5 py-3.5">
                    <p className="font-mono font-semibold text-espresso">{inr(o.total)}</p>
                    {o.payment_status === "paid" ? (
                      <span data-testid={`order-payment-paid-${o.id}`} className="mt-1 inline-block rounded-full bg-sage-light px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-sage">Paid</span>
                    ) : o.payment_method === "cod" ? (
                      <span data-testid={`order-payment-cod-${o.id}`} className="mt-1 inline-block rounded-full bg-ochre/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-ochre">COD · collect {inr(o.total)}</span>
                    ) : (
                      <span className="mt-1 inline-block rounded-full bg-terra-light px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-terra-dark">Unpaid</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest ${STATUS_STYLE[o.status] || ""}`}>{o.status}</span>
                      <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                        <SelectTrigger data-testid={`order-status-select-${o.id}`} className="h-8 w-32 border-linen text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.keys(STATUS_STYLE).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-1.5">
                      {canShip(o) && (
                        <button
                          onClick={() => { setShipOrder(o); setDims({ length_cm: 20, breadth_cm: 15, height_cm: 10, weight_kg: 0.5 }); }}
                          data-testid={`order-ship-${o.id}`}
                          className="flex items-center gap-1.5 rounded-full bg-terra px-3.5 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-terra-dark"
                        >
                          <Truck className="h-3.5 w-3.5" /> Ship
                        </button>
                      )}
                      <button onClick={() => setViewing(o)} className="rounded-lg p-2 text-clay/60 transition-colors hover:bg-sand hover:text-espresso" aria-label={`View order ${o.id}`} data-testid={`order-view-${o.id}`}>
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Order details */}
      <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-cream sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-espresso">Order #{viewing?.id}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-5">
              {viewing.shipping_awb && (
                <div className="rounded-xl border border-sage/40 bg-sage-light/60 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-sage">Shipment</p>
                  <p className="mt-1.5 text-sm text-espresso">{viewing.shipping_courier} · AWB <span className="font-mono font-semibold">{viewing.shipping_awb}</span></p>
                  <a href={viewing.shipping_tracking_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs font-semibold text-terra underline">
                    Track shipment
                  </a>
                </div>
              )}
              <div className="rounded-xl border border-linen bg-white p-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay/60">Deliver to</p>
                <p className="mt-2 text-sm leading-relaxed text-espresso">
                  {viewing.customer.name} · {viewing.customer.phone}<br />
                  {viewing.customer.email}<br />
                  {viewing.customer.address}{viewing.customer.landmark ? `, near ${viewing.customer.landmark}` : ""}<br />
                  {viewing.customer.city}, {viewing.customer.state} — {viewing.customer.pincode}
                </p>
              </div>
              <ul className="divide-y divide-linen rounded-xl border border-linen bg-white px-4">
                {viewing.items.map((i) => (
                  <li key={i.product_id + String(i.freebie || "")} className="flex items-center gap-3 py-3">
                    <img src={i.image} alt="" className="h-11 w-11 rounded-lg object-cover" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-espresso">{i.name}</p>
                      <p className="font-mono text-xs text-clay/60">× {i.qty}{i.freebie ? " · free gift" : ""}</p>
                    </div>
                    <span className="font-mono text-sm">{i.freebie ? "₹0" : inr(i.line_total)}</span>
                  </li>
                ))}
              </ul>
              <div className="rounded-xl border border-linen bg-white p-4 text-sm">
                <div className="flex justify-between text-clay/70"><span>Subtotal</span><span className="font-mono">{inr(viewing.subtotal)}</span></div>
                {viewing.discount > 0 && <div className="mt-1 flex justify-between text-sage"><span>Coupon {viewing.coupon_code}</span><span className="font-mono">−{inr(viewing.discount)}</span></div>}
                <div className="mt-1 flex justify-between text-clay/70"><span>Shipping</span><span className="font-mono">{viewing.shipping === 0 ? "Free" : inr(viewing.shipping)}</span></div>
                <div className="mt-2 flex justify-between border-t border-linen pt-2 font-semibold text-espresso"><span>Total</span><span className="font-mono">{inr(viewing.total)}</span></div>
                <p className="mt-3 border-t border-dashed border-linen pt-3 text-xs text-clay/60">{viewing.payment_note}</p>
                {viewing.payment_method === "cod" && viewing.payment_status !== "paid" && (
                  <button
                    onClick={() => markPaid(viewing.id)}
                    data-testid={`order-mark-paid-${viewing.id}`}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-sage px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:opacity-90"
                  >
                    <Banknote className="h-3.5 w-3.5" /> Mark COD payment collected
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dispatch dialog */}
      <Dialog open={!!shipOrder} onOpenChange={() => setShipOrder(null)}>
        <DialogContent className="bg-cream sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-espresso">Ship order #{shipOrder?.id}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-clay/70">A shipment will be created on Shiprocket from <span className="font-semibold">{shiprocket.pickup_location}</span>, the customer gets a tracking email, and the order moves to Dispatched.</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {[["length_cm", "Length (cm)"], ["breadth_cm", "Breadth (cm)"], ["height_cm", "Height (cm)"], ["weight_kg", "Weight (kg)"]].map(([k, l]) => (
              <div key={k}>
                <label className="mb-1.5 block text-xs font-semibold text-clay">{l}</label>
                <input type="number" step="0.1" min="0.1" value={dims[k]} onChange={(e) => setDims((d) => ({ ...d, [k]: e.target.value }))} className={inputCls} data-testid={`dispatch-${k}`} />
              </div>
            ))}
          </div>
          <button
            data-testid="dispatch-confirm-button"
            onClick={dispatch}
            disabled={shipping}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-terra py-3.5 text-sm font-semibold text-white transition-colors hover:bg-terra-dark disabled:opacity-50"
          >
            {shipping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
            {shipping ? "Creating shipment…" : "Create shipment & notify customer"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersTab;
